import React from 'react'

import { useMarket } from '@/shared/market'
import { pxToRem } from '@/shared/styles/themes'
import { getTermsData } from '@/slices/Help/mocks/terms/data'
import { Box, Container, styled, Typography } from '@mui/material'

export const TermsPageSlice: React.FC = () => {
  const { market } = useMarket()
  const termsData = getTermsData(market)

  return (
    <StyledPageWrapper>
      <Container maxWidth="md">
        <StyledHeader>
          <StyledTitle variant="h1">
            Terms and Conditions
          </StyledTitle>
          <StyledSubtitle variant="body1">
            Last updated: February 21, 2026
          </StyledSubtitle>
        </StyledHeader>

        <StyledTermsContent>
          {termsData.map((section) => (
            <StyledSection key={section.id}>
              <StyledSectionTitle variant="h2">
                {section.id}. {section.title}
              </StyledSectionTitle>
              {section.content.map((paragraph, index) => (
                <StyledParagraph key={index} variant="body1">
                  {paragraph}
                </StyledParagraph>
              ))}
            </StyledSection>
          ))}
        </StyledTermsContent>

        <StyledFooterSection>
          <Typography variant="h6" gutterBottom>
            Questions about our terms?
          </Typography>
          <Typography variant="body1">
            Contact us at{' '}
            <StyledLink href="mailto:support@pixie.education">
              support@pixie.education
            </StyledLink>
          </Typography>
        </StyledFooterSection>
      </Container>
    </StyledPageWrapper>
  )
}

const StyledPageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: pxToRem(40),
  paddingBottom: pxToRem(80),

  [theme.breakpoints.up('md')]: {
    paddingTop: pxToRem(60),
    paddingBottom: pxToRem(120),
  },
}))

const StyledHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: pxToRem(40),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(60),
  },
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: pxToRem(16),
  fontSize: pxToRem(32),
  fontWeight: 700,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
    marginBottom: pxToRem(20),
  },
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(14),
  opacity: 0.7,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
  },
}))

const StyledTermsContent = styled(Box)(({ theme }) => ({
  marginBottom: pxToRem(60),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(80),
  },
}))

const StyledSection = styled(Box)(({ theme }) => ({
  marginBottom: pxToRem(32),

  '&:last-child': {
    marginBottom: 0,
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(40),
  },
}))

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(20),
  fontWeight: 600,
  marginBottom: pxToRem(12),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(24),
    marginBottom: pxToRem(16),
  },
}))

const StyledParagraph = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: pxToRem(14),
  lineHeight: 1.7,
  marginBottom: pxToRem(12),

  '&:last-child': {
    marginBottom: 0,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
    marginBottom: pxToRem(14),
  },
}))

const StyledFooterSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: pxToRem(15),
  backgroundColor: theme.palette.background.paper,
  borderRadius: pxToRem(12),
  border: `1px solid ${theme.palette.divider}`,

  '& h6': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  '& .MuiTypography-body1': {
    color: theme.palette.primary.main,
  },

  [theme.breakpoints.up('md')]: {
    padding: pxToRem(32),
  },
}))

const StyledLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 600,

  '&:hover': {
    textDecoration: 'underline',
  },
}))
